import { ApiResponse, ApisauceInstance } from "apisauce";
import { ApiConstant, KeyConstant } from "const";
import QueryString from "qs";
import { StorageUtil } from "utils";

// Defined trios api list
const createTriosApi = (api: ApisauceInstance): ITriosApi => {
  const apiHeaders = {};

  if (ApiConstant.TRIOS_API_VERSION) {
    apiHeaders["trios-api-version"] = ApiConstant.TRIOS_API_VERSION;
  }

  const { serviceId } = StorageUtil.getCommonKey(KeyConstant.KEY_TMP_BRANCH) || {};
  if (serviceId) {
    apiHeaders["Service-id"] = serviceId;
  }

  api.setHeaders({ ...api.headers, ...apiHeaders });

  return {
    // ------------------------ Account Api --------------------------
    updateAccount: data =>
      api.post(
        ApiConstant.POST_UPDATE_ACCOUNT,
        QueryString.stringify(data, {
          skipNulls: true,
        }),
      ),

    getAccountByIds: data => api.post(ApiConstant.POST_GET_ACCOUNT, QueryString.stringify(data, { skipNulls: true })),

    getAccountList: data => api.get(ApiConstant.GET_ACCOUNT_LIST, data),

    uploadFileAccount: data =>
      api.post(ApiConstant.POST_UPLOAD_FILE_ACCOUNT, data, {
        headers: ApiConstant.HEADER_DEFAULT_UPLOAD_FILE,
      }),

    blockAccount: data => api.post(ApiConstant.POST_BlOCK_ACCOUNT, QueryString.stringify(data, { skipNulls: true })),

    unblockAccount: data => api.post(ApiConstant.POST_UNBLOCK_USER, QueryString.stringify(data, { skipNulls: true })),

    // ------------------------ Account Device Api ---------------------
    getDevice: data => api.post(ApiConstant.POST_GET_DEVICE, QueryString.stringify(data, { skipNulls: true })),

    deleteDevice: data => api.post(ApiConstant.POST_DELETE_DEVICE, QueryString.stringify(data, { skipNulls: true })),

    updateDevice: data => api.post(ApiConstant.POST_UPDATE_DEVICE, QueryString.stringify(data, { skipNulls: true })),

    // ------------------------ Account Key Api ------------------------
    uploadKeys: data => api.post(ApiConstant.POST_UPLOAD_KEY_LIST, QueryString.stringify(data, { skipNulls: true })),

    getKeys: data => api.post(ApiConstant.POST_GET_KEY, QueryString.stringify(data, { skipNulls: true })),

    // ------------------------ Account Contact Api --------------------
    getContactList: data => api.get(ApiConstant.GET_CONTACT_LIST, data),

    getContact: (account_id, contact_id) => api.get(ApiConstant.GET_CONTACT, { account_id, contact_id }),

    updateContact: data => api.post(ApiConstant.POST_UPDATE_CONTACT, QueryString.stringify(data, { skipNulls: true })),

    addContact: data => api.post(ApiConstant.POST_ADD_CONTACT, QueryString.stringify(data, { skipNulls: true })),

    deleteContact: contact_id => api.post(ApiConstant.POST_DELETE_CONTACT, QueryString.stringify({ contact_id })),

    // ------------------------ Branch Api -----------------------------
    getBranchList: () => api.post(ApiConstant.POST_GET_BRANCH_LIST),

    getBranchAccount: data =>
      api.post(ApiConstant.POST_GET_BRANCH_ACCOUNT, QueryString.stringify(data, { skipNulls: true })),

    updateBranch: data =>
      api.post(ApiConstant.POST_UPDATE_BRANCH_ACCOUNT, QueryString.stringify(data, { skipNulls: true })),

    syncMappingBranch: data => api.get(ApiConstant.GET_BRANCH_MAPPING, data),

    createBranchMapping: data => api.post(ApiConstant.POST_BRANCH_MAPPING, QueryString.stringify(data)),

    // ----- [Conversation] Group Api - Include channel, group, personal -----
    addConversation: data =>
      api.post(ApiConstant.POST_CREATE_CONVERSATION, QueryString.stringify(data, { skipNulls: true })),

    getConversationList: data => api.get(ApiConstant.GET_CONVERSATION_LIST, data),

    getConversationMember: data => api.post(ApiConstant.POST_GET_CONVERSATION_MEMBER, QueryString.stringify(data)),
    updateConversation: data =>
      api.post(ApiConstant.POST_UPDATE_CONVERSATION, QueryString.stringify(data, { skipNulls: true })),

    deleteConversation: data =>
      api.post(ApiConstant.POST_DELETE_CONVERSATION, QueryString.stringify(data, { skipNulls: true })),

    addConversationMembers: data =>
      api.post(ApiConstant.POST_ADD_MEMBERS, QueryString.stringify(data, { skipNulls: true })),

    // ------------------------ Call Api ------------------------------
    getCallingStatus: data => api.get(ApiConstant.GET_CALL_STATUS, data),

    getCallHistory: data => api.get(ApiConstant.GET_CALL_HISTORY, data),

    updateCallHistory: data =>
      api.post(ApiConstant.POST_UPDATE_CALL_HISTORY, QueryString.stringify(data, { skipNulls: true })),

    // ------------------------ Message Api ---------------------------
    sendMessage: data => {
      const isSendMessageFailed = StorageUtil.getCommonKey(KeyConstant.KEY_SEND_MESSAGE_FAIL);
      if (isSendMessageFailed) {
        return Promise.resolve({
          status: ApiConstant.STT_INTERNAL_SERVER,
          message: "Simulated internal server error",
        } as unknown as ApiResponse<any>);
      } else {
        return api.post(ApiConstant.POST_SEND_MESSAGE, QueryString.stringify(data, { skipNulls: true }), {
          timeout: 600000,
        });
      }
    },

    sendMessageFile: data =>
      api.post(ApiConstant.POST_SEND_MESSAGE_FILE, data, {
        headers: ApiConstant.HEADER_DEFAULT_UPLOAD_FILE,
      }),

    getMessageList: data => api.get(ApiConstant.GET_MESSAGE_LIST, data),

    updateMessageStatus: data =>
      api.post(ApiConstant.POST_UPDATE_MESSAGE_STT, QueryString.stringify(data, { skipNulls: true })),

    // Get seen_member, received_member of message
    getMessageDetail: data => api.get(ApiConstant.GET_MESSAGE_DETAIL, data),

    // ---------------------- Backup/ Restore data ---------------------
    requestVerifyChangeDeviceRole: data =>
      api.post(ApiConstant.POST_VERIFY_OTP_CHANGE_DEVICE_ROLE, QueryString.stringify(data, { skipNulls: true })),

    requestBackupUpload: data =>
      api.post(ApiConstant.POST_BACKUP_UPLOAD, data, {
        headers: ApiConstant.HEADER_DEFAULT_UPLOAD_FILE,
      }),

    requestBackup: data => api.post(ApiConstant.POST_BACKUP, QueryString.stringify(data, { skipNulls: true })),

    requestGetBackup: data => api.get(ApiConstant.GET_BACKUP_INFO, data),

    // ------------------------ JitsiMeet -------------------------------
    postMeetToken: data => api.post(ApiConstant.POST_MEET_TOKEN, QueryString.stringify(data, { skipNulls: true })),

    // ------------------------ Other Api -------------------------------
    getEmoji: data => api.get(ApiConstant.GET_EMOJI, data),

    // ------------------------ ThirdParty Api --------------------------
    createThirdPartyToken: service_id =>
      api.post(ApiConstant.POST_CREATE_THIRD_TOKEN, QueryString.stringify({ service_id })),

    // ------------------------ Checking Server Status --------------------------
    checkingServerStatus: () => api.get(ApiConstant.GET_SERVER_TIME, {}, { timeout: 2000 }),

    // ------------------------ PubSub ----------------------------------
    checkEvents: device_id => api.get(ApiConstant.GET_CHECK_EVENT, { device_id, is_online: true }),

    postEventsFinish: data => api.post(ApiConstant.POST_EVENT_FINISH, QueryString.stringify(data, { skipNulls: true })),

    getEventsList: data => api.get(ApiConstant.POST_EVENT_LIST, data),

    // ------------------------ Notification ----------------------------------
    getNotificationList: data => api.get(ApiConstant.GET_NOTIFICATION_LIST, data),

    // ------------------------ Multiple File ----------------------------------
    getFilesInfo: data => api.post(ApiConstant.GET_FILES_INFO, QueryString.stringify(data, { skipNulls: true })),

    getFileTus: (domain, url, groupId, onDownloadProgressCallback) => {
      // Todo pj47
      if (onDownloadProgressCallback) {
        onDownloadProgressCallback({
          bytesDownloaded: 0,
          bytesTotal: 0,
        });
      }
      const isDownloadFileFailed = StorageUtil.getCommonKey(KeyConstant.KEY_DOWNLOAD_FILE_FAIL);
      if (isDownloadFileFailed) {
        return new Promise(resolve => {
          setTimeout(() => {
            resolve({
              status: ApiConstant.STT_INTERNAL_SERVER,
              message: "Simulated internal server error",
            } as unknown as ApiResponse<any>);
          }, 2000); // Dừng 2 giây (2000ms)
        });
      } else {
        return api.get(
          url,
          {},
          {
            baseURL: domain,
            headers: {
              group_id: groupId,
            },
            responseType: "arraybuffer",
            timeout: 15 * 60 * 1000, // 15 minutes
            onDownloadProgress: progressEvent => {
              const bytesDownloaded = progressEvent.loaded;
              const bytesTotal = progressEvent.total || 0;

              if (onDownloadProgressCallback) {
                onDownloadProgressCallback({
                  bytesDownloaded,
                  bytesTotal,
                });
              }
            },
          },
        );
      }
    },

    postFileStatus: data => api.post(ApiConstant.POST_FILE_UPDATE, QueryString.stringify(data, { skipNulls: true })),

    // Pin message
    pinMessage: data => api.post(ApiConstant.POST_PIN_MESSAGE, QueryString.stringify(data, { skipNulls: true })),

    getPinList: data => api.post(ApiConstant.GET_PIN_LIST, QueryString.stringify(data, { skipNulls: true })),

    // Login by QR code: Manage Request
    getRequestList: data => api.post(ApiConstant.POST_REQUEST_LIST, QueryString.stringify(data, { skipNulls: true })),

    updateRequest: data => api.post(ApiConstant.POST_REQUEST_UPDATE, QueryString.stringify(data, { skipNulls: true })),

    // Get list Unit, Position, Department
    getListUnit: (branch_id, pagination) => api.get(ApiConstant.GET_UNIT_LIST, { branch_id, ...pagination }),

    getListDepartment: (branch_id, unit_id, pagination) =>
      api.get(ApiConstant.GET_DEPARTMENT_LIST, { branch_id, unit_id, ...pagination }),

    getListPosition: params => api.get(ApiConstant.GET_LIST_POSITIONS, params),

    // ------------------------ Notification Setting ----------------------------------
    // Required: setting_sub_type, state
    updateAccountNotiSetting: data =>
      api.post(ApiConstant.POST_UPDATE_ACCOUNT_NOTI_SETTING, QueryString.stringify(data, { skipNulls: true })),

    getAccountNotiSetting: data =>
      api.post(ApiConstant.POST_GET_ACCOUNT_NOTI_SETTING, QueryString.stringify(data, { skipNulls: true })),

    // Required: group_id, setting_sub_type, created, state
    updateGroupNotiSetting: data =>
      api.post(ApiConstant.POST_UPDATE_GROUP_NOTI_SETTING, QueryString.stringify(data, { skipNulls: true })),

    getGroupNotiSetting: data =>
      api.post(ApiConstant.POST_GET_GROUP_NOTI_SETTING, QueryString.stringify(data, { skipNulls: true })),

    // Required: thread_id, setting_sub_type, created, state
    updateThreadNotiSetting: data =>
      api.post(ApiConstant.POST_UPDATE_THREAD_NOTI_SETTING, QueryString.stringify(data, { skipNulls: true })),

    getThreadNotiSetting: data =>
      api.post(ApiConstant.POST_GET_THREAD_NOTI_SETTING, QueryString.stringify(data, { skipNulls: true })),

    // ------------------------ Delete group only me ----------------------------------
    // Required: group_id
    postDeleteGroupOnlyMe: data =>
      api.post(ApiConstant.POST_DELETE_GROUP_ONLY_ME, QueryString.stringify(data, { skipNulls: true })),

    getServiceAdmin: (data: any) =>
      api.post(
        ApiConstant.POST_SERVICE_ADMIN_LIST,
        QueryString.stringify(data, {
          skipNulls: true,
        }),
      ),
  };
};
export default createTriosApi;

export interface ITriosApi {
  updateAccount: (data: any) => Promise<ApiResponse<any>>;
  getAccountByIds: (data: any) => Promise<ApiResponse<any>>;
  getAccountList: (data: any) => Promise<ApiResponse<any>>;
  uploadFileAccount: (data: any) => Promise<ApiResponse<any>>;
  blockAccount: (data: any) => Promise<ApiResponse<any>>;
  unblockAccount: (data: any) => Promise<ApiResponse<any>>;
  getDevice: (data: any) => Promise<ApiResponse<any>>;
  deleteDevice: (data: any) => Promise<ApiResponse<any>>;
  updateDevice: (data: any) => Promise<ApiResponse<any>>;
  uploadKeys: (data: any) => Promise<ApiResponse<any>>;
  getKeys: (data: any) => Promise<ApiResponse<any>>;
  getContactList: (data: any) => Promise<ApiResponse<any>>;
  getContact: (account_id: string, contact_id: string) => Promise<ApiResponse<any>>;
  updateContact: (data: any) => Promise<ApiResponse<any>>;
  addContact: (data: any) => Promise<ApiResponse<any>>;
  deleteContact: (contact_id: string) => Promise<ApiResponse<any>>;
  getBranchList: () => Promise<ApiResponse<any>>;
  getBranchAccount: (data: any) => Promise<ApiResponse<any>>;
  updateBranch: (data: any) => Promise<ApiResponse<any>>;
  syncMappingBranch: (data: any) => Promise<ApiResponse<any>>;
  createBranchMapping: (data: any) => Promise<ApiResponse<any>>;
  addConversation: (data: any) => Promise<ApiResponse<any>>;
  getConversationList: (data: any) => Promise<ApiResponse<any>>;
  getConversationMember: (data: any) => Promise<ApiResponse<any>>;
  updateConversation: (data: any) => Promise<ApiResponse<any>>;
  deleteConversation: (data: any) => Promise<ApiResponse<any>>;
  addConversationMembers: (data: any) => Promise<ApiResponse<any>>;
  getCallingStatus: (data: any) => Promise<ApiResponse<any>>;
  getCallHistory: (data: any) => Promise<ApiResponse<any>>;
  updateCallHistory: (data: any) => Promise<ApiResponse<any>>;
  sendMessage: (data: any) => Promise<ApiResponse<any>>;
  sendMessageFile: (data: any) => Promise<ApiResponse<any>>;
  getMessageList: (data: any) => Promise<ApiResponse<any>>;
  updateMessageStatus: (data: any) => Promise<ApiResponse<any>>;
  getMessageDetail: (data: any) => Promise<ApiResponse<any>>;
  requestVerifyChangeDeviceRole: (data: any) => Promise<ApiResponse<any>>;
  requestBackupUpload: (data: any) => Promise<ApiResponse<any>>;
  requestBackup: (data: any) => Promise<ApiResponse<any>>;
  requestGetBackup: (data: any) => Promise<ApiResponse<any>>;
  postMeetToken: (data: any) => Promise<ApiResponse<any>>;
  getEmoji: (data: any) => Promise<ApiResponse<any>>;
  createThirdPartyToken: (service_id: string) => Promise<ApiResponse<any>>;
  checkingServerStatus: () => Promise<ApiResponse<any>>;
  checkEvents: (device_id: string) => Promise<ApiResponse<any>>;
  postEventsFinish: (data: any) => Promise<ApiResponse<any>>;
  getEventsList: (data: any) => Promise<ApiResponse<any>>;
  getNotificationList: (data: any) => Promise<ApiResponse<any>>;
  getFilesInfo: (data: any) => Promise<ApiResponse<any>>;
  getFileTus: (
    domain: string,
    url: string,
    groupId: string,
    onDownloadProgressCallback: any,
  ) => Promise<ApiResponse<any>>;
  postFileStatus: (data: any) => Promise<ApiResponse<any>>;
  pinMessage: (data: any) => Promise<ApiResponse<any>>;
  getPinList: (data: any) => Promise<ApiResponse<any>>;
  getRequestList: (data: any) => Promise<ApiResponse<any>>;
  updateRequest: (data: any) => Promise<ApiResponse<any>>;
  getListUnit: (branch_id: string, pagination: any) => Promise<ApiResponse<any>>;
  getListDepartment: (branch_id: string, unit_id: string, pagination: any) => Promise<ApiResponse<any>>;
  getListPosition: (params: any) => Promise<ApiResponse<any>>;
  updateAccountNotiSetting: (data: any) => Promise<ApiResponse<any>>;
  getAccountNotiSetting: (data: any) => Promise<ApiResponse<any>>;
  updateGroupNotiSetting: (data: any) => Promise<ApiResponse<any>>;
  getGroupNotiSetting: (data: any) => Promise<ApiResponse<any>>;
  updateThreadNotiSetting: (data: any) => Promise<ApiResponse<any>>;
  getThreadNotiSetting: (data: any) => Promise<ApiResponse<any>>;
  postDeleteGroupOnlyMe: (data: any) => Promise<ApiResponse<any>>;
  getServiceAdmin: (data: any) => Promise<any>;
}
